import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield, Users, CheckCircle, Plus, Search, RefreshCw, Eye,
  Volume2, X, MoreVertical, RotateCcw, Ban, Pencil,
  ChevronLeft, ChevronRight, Sparkles, ArrowRight, UserX, Copy,
} from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';
import { toast } from 'sonner';

// ════════════════════════════════════════════════════════════════
// DESIGN TOKENS — CSS Variables reais do sistema (index.css :root --dp-*)
// ════════════════════════════════════════════════════════════════
const DS = {
  accent: 'var(--dp-blue-500, #0DAEFF)',
  bgPage: 'var(--dp-bg-primary, #141414)',
  bgCard: 'var(--dp-bg-card, #242424)',
  bgElevated: 'var(--dp-bg-elevated, #303030)',
  bgInput: 'var(--dp-bg-input, #1A1A1A)',
  textPrimary: 'var(--dp-text-primary, #F5F5F5)',
  textSecondary: 'var(--dp-text-secondary, #A0A0A0)',
  textTertiary: 'var(--dp-text-tertiary, #666666)',
  borderSubtle: 'var(--dp-border-subtle, rgba(255, 255, 255, 0.06))',
  borderMedium: 'var(--dp-border-medium, rgba(255, 255, 255, 0.10))',
  font: "var(--dp-font, 'Outfit', system-ui, -apple-system, sans-serif)",
  radius: '12px',
  radiusSm: '8px',
  radiusLg: '16px',
  shadowCard: '0 4px 24px rgba(0, 0, 0, 0.2)',
  shadowHover: '0 8px 32px rgba(0, 0, 0, 0.3)',
  green: '#34D399',
  yellow: '#FBBF24',
  orange: '#FB923C',
  red: '#F87171',
  purple: '#A78BFA',
  blue: '#60A5FA',
  pink: '#F472B6',
};

// ════════════════════════════════════════════════════════════════
// CONFIG — status, planos, origem, motivos, eventos
// ════════════════════════════════════════════════════════════════
// Estado EFETIVO (vem do backend: estado_efetivo) — cores coerentes do sistema.
const estadoConfig = {
  PREMIUM:     { label: 'PREMIUM',     color: DS.green },
  // EXPIRADO colapsou em CANCELADO (backend). Redirect defensivo: se alguma linha legada
  // ainda vier 'EXPIRADO', exibe como CANCELADO (nunca o texto "EXPIRADO").
  EXPIRADO:    { label: 'CANCELADO',   color: DS.purple },
  CANCELADO:   { label: 'CANCELADO',   color: DS.purple },
  REEMBOLSADO: { label: 'REEMBOLSADO', color: DS.pink },
  FREE:        { label: 'FREE',        color: DS.textSecondary },
};

const planConfig = {
  free:    { label: 'FREE',    color: '#9CA3AF' },
  trial:   { label: 'TRIAL',   color: '#8b5cf6' },
  basic:   { label: 'BASIC',   color: '#6b7280' },
  lite:    { label: 'LITE',    color: '#3b82f6' },
  starter: { label: 'STARTER', color: '#f59e0b' },
  pro:     { label: 'PRO',     color: '#7c3aed' },
  plus:    { label: 'PLUS',    color: '#ec4899' },
  creator: { label: 'CREATOR', color: '#10b981' },
  trimestral: { label: 'TRIMESTRAL', color: '#14b8a6' },
  master:  { label: 'MASTER',  color: '#f97316' },
};

const sourceMeta = {
  cakto:      { label: 'Cakto', color: '#0DAEFF' },
  kirvano:    { label: 'Kirvano', color: '#A78BFA' },
  perfectpay: { label: 'PerfectPay', color: '#34D399' },
  manual:     { label: 'Manual', color: '#FBBF24' },
  admin:      { label: 'Admin', color: '#60A5FA' },
};

// Cor do ponto na timeline da jornada, por kind (vindo do backend: journey[].kind).
// 'expired' agora mapeia para o kind 'cancelou' no backend → não há mais kind 'expirou'.
const journeyKindColor = {
  cadastro: DS.textTertiary, comprou: DS.green, recomprou: DS.green, renovou: DS.blue, mudou_plano: DS.blue,
  reativou: DS.green, cancelou: DS.purple, reembolsou: DS.pink,
  chargeback: DS.red, falha: DS.red, offer_fallback: DS.yellow,
};

const subStatusMeta = {
  active:      { label: 'Ativa', color: DS.green },
  encerrada:   { label: 'Encerrada', color: DS.textTertiary },   // CORREÇÃO A: 'active' c/ período vencido (vigência)
  canceled:    { label: 'Cancelada', color: DS.orange },
  refunded:    { label: 'Reembolsada', color: DS.pink },
  chargedback: { label: 'Chargeback', color: DS.red },
  expired:     { label: 'Cancelada', color: DS.textTertiary },   // sub-status Cakto legado → exibe unificado
};

// Dropdown options
const estadoOptions = [
  { value: '', label: 'Todos os estados' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'REEMBOLSADO', label: 'Reembolsado' },
  { value: 'FREE', label: 'Free (puro)' },
];
const planOptions = [
  { value: '', label: 'Todos os Planos' },
  { value: 'pro', label: 'PRO' },
  { value: 'plus', label: 'PLUS' },
  { value: 'basic', label: 'BASIC' },
  { value: 'lite', label: 'LITE' },
  { value: 'free', label: 'FREE' },
];
const sourceOptions = [
  { value: '', label: 'Todas as Origens' },
  { value: 'cakto', label: 'Cakto' },
  { value: 'kirvano', label: 'Kirvano' },
  { value: 'perfectpay', label: 'PerfectPay' },
  { value: 'manual', label: 'Manual' },
  { value: 'admin', label: 'Admin' },
];
// Só os 5 planos reais do sistema (legados removidos das opções de escolha).
const addPlanOptions = [
  { value: 'free', label: 'FREE' },
  { value: 'basic', label: 'BASIC (Mensal — 30 dias)' },
  { value: 'lite', label: 'LITE (Mensal — 30 dias)' },
  { value: 'pro', label: 'PRO (Mensal — 30 dias)' },
  { value: 'plus', label: 'PLUS (Mensal — 30 dias)' },
];
const PLAN_CHIPS = ['pro', 'plus', 'basic', 'lite', 'free'];
const PAGE_SIZE = 50;

// ════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return '—'; }
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
};
const fmtMoney = (cents) => {
  if (cents === null || cents === undefined || cents === '') return '—';
  const n = Number(cents);
  if (Number.isNaN(n)) return '—';
  return (n / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const formatPhone = (phone) => {
  if (!phone) return null;
  const d = phone.replace(/\D/g, '');
  if (d.length === 13) return `(${d.slice(0, 2)}) ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `(${d.slice(0, 2)}) ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
};

/** Texto/cor de renovação — usa dias_para_renovar do backend (fuso BR). */
const getRenewalInfo = (item) => {
  if (item.status !== 'ativo') {
    const m = item.motivo_inativacao;
    if (m === 'reembolsado') return { text: 'Reembolsado', color: DS.pink };
    if (m === 'chargeback') return { text: 'Chargeback', color: DS.red };
    if (m === 'cancelado_admin' || m === 'suspenso') return { text: 'Cancelado por admin', color: DS.purple };
    if (m === 'cancelado_usuario') return { text: 'Cancelado pelo usuário', color: DS.orange };
    if (item.end_date) return { text: `Expirou ${fmtDate(item.end_date)}`, color: DS.red };
    return { text: 'Inativo', color: DS.red };
  }
  const d = item.dias_para_renovar;
  if (d === null || d === undefined) return null;
  if (item.cancellation_requested) {
    if (d <= 0) return { text: 'Vence hoje', color: DS.orange };
    return { text: `Cancela em ${d}d`, color: DS.orange };
  }
  if (d < 0) return { text: `Vencido há ${Math.abs(d)}d`, color: DS.red };
  if (d === 0) return { text: 'Vence hoje', color: DS.orange };
  if (item.auto_renew === false) return { text: `Expira em ${d}d`, color: DS.orange };
  if (d <= 3) return { text: `Renova em ${d}d`, color: DS.orange };
  return { text: `Renova em ${d} dias`, color: DS.green };
};

/** Método de pagamento legível (Cakto). */
const fmtPayment = (ae) => {
  if (!ae) return null;
  if (ae.cakto_card_brand || ae.cakto_card_last_digits) {
    const brand = (ae.cakto_card_brand || 'Cartão');
    return `${brand.charAt(0).toUpperCase() + brand.slice(1)} •••• ${ae.cakto_card_last_digits || '----'}`;
  }
  const pm = ae.cakto_payment_method;
  if (!pm) return null;
  const map = { pix: 'PIX', credit_card: 'Cartão de crédito', credit: 'Cartão', boleto: 'Boleto' };
  return map[pm] || pm;
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const getApiUrl = () => {
  const base = BACKEND_URL || window.location.origin;
  return base.endsWith('/') ? base.slice(0, -1) : base;
};
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Jornada DETALHADA — label/cor por change_type (vem do backend: jornada_eventos[].change_type).
// Cobre TODOS os change_types gravados no subscription_history (nenhum silenciado).
const jornadaConfig = {
  cadastro:                { label: 'Cadastro realizado', color: DS.textTertiary },
  created:                 { label: 'Compra autorizada', color: DS.green },
  repurchase:              { label: 'Compra autorizada', color: DS.green },
  renewal:                 { label: 'Renovação', color: DS.blue },
  renewed:                 { label: 'Renovação', color: DS.blue },
  granted_admin:           { label: 'Plano concedido pelo admin', color: DS.green },
  plan_changed:            { label: 'Mudou de plano', color: DS.blue },
  upgrade:                 { label: 'Mudou de plano', color: DS.blue },
  downgrade:               { label: 'Mudou de plano', color: DS.blue },
  reactivated:             { label: 'Reativado pelo admin', color: DS.green },
  cancelled:               { label: 'Plano cancelado', color: DS.purple },
  cancellation_scheduled:  { label: 'Cancelamento confirmado', color: DS.orange },
  auto_renew_disabled:     { label: 'Cancelamento confirmado', color: DS.orange },
  cancellation_request_app:       { label: 'Solicitou cancelamento no app', color: DS.orange },
  cancellation_request_withdrawn: { label: 'Desfez a solicitação de cancelamento', color: DS.textTertiary },
  expired:                 { label: 'Plano encerrado', color: DS.purple },
  refunded:                { label: 'Reembolso realizado', color: DS.pink },
  refunded_access_kept:    { label: 'Reembolso realizado', color: DS.pink },
  chargeback:              { label: 'Chargeback', color: DS.red },
  chargedback_access_kept: { label: 'Chargeback', color: DS.red },
  payment_failed:          { label: 'Falha de pagamento', color: DS.red },
  offer_fallback:          { label: 'Ativação por fallback (revisar oferta)', color: DS.yellow },
};
const jornadaOrigemColor = (o) => (o === 'Admin/Manual' ? DS.blue : (o === 'Sistema' ? DS.textTertiary : '#0DAEFF'));
const jornadaSublabel = (e) => {
  if ((e.change_type === 'cancellation_scheduled' || e.change_type === 'auto_renew_disabled') && e.end_date) return `Acesso até ${fmtDate(e.end_date)}`;
  if ((e.change_type === 'created' || e.change_type === 'repurchase') && e.sem_cadastro) return 'Usuário ainda sem cadastro';
  if ((e.change_type === 'plan_changed' || e.change_type === 'upgrade' || e.change_type === 'downgrade') && e.old_plan && e.old_plan !== e.plan) return `${String(e.old_plan).toUpperCase()} → ${String(e.plan || '').toUpperCase()}`;
  return null;
};

// ════════════════════════════════════════════════════════════════
// SMALL COMPONENTS
// ════════════════════════════════════════════════════════════════
// Badge do ESTADO EFETIVO (PREMIUM/CANCELADO/REEMBOLSADO/FREE) — nunca o status cru.
const EstadoBadge = ({ estado, motivo, cancellationRequested, autoRenew, cancelRequestPending, endDate, diasParaRenovar }) => {
  const cfg = estadoConfig[estado] || { label: estado || '—', color: DS.textSecondary };
  const isPremium = estado === 'PREMIUM';
  // CONFIRMADO pela Cakto: o webhook subscription_canceled chegou
  // (cancellation_requested OU auto_renew=false). Acesso segue PREMIUM até end_date.
  const cancelConfirmado = isPremium && (cancellationRequested || autoRenew === false);
  // PEDIDO no app, AINDA sem webhook: o usuário solicitou e o admin precisa cancelar
  // na Cakto. Some assim que o webhook confirma (o backend limpa cancel_request_pending).
  const pedidoPendente = isPremium && !cancelConfirmado && cancelRequestPending;
  const d = diasParaRenovar;
  const cancelaTxt = (d === null || d === undefined)
    ? `Cancela${endDate ? ` · até ${fmtDate(endDate)}` : ''}`
    : (d <= 0 ? 'Cancela hoje' : `Cancela em ${d}d`);
  const seloBase = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase',
    padding: '2px 7px', borderRadius: '5px', marginTop: '1px', whiteSpace: 'nowrap', fontFamily: DS.font,
  };
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '6px',
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: cfg.color, background: `${cfg.color}1A`,
        border: `1px solid ${cfg.color}40`, fontFamily: DS.font, textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
        {cfg.label}
      </span>
      {estado !== 'PREMIUM' && estado !== 'FREE' && motivo && (
        <span style={{ fontSize: '10px', color: DS.textTertiary, paddingLeft: '4px' }}>{motivo}</span>
      )}
      {cancelConfirmado && (
        <span style={{ ...seloBase, color: DS.orange, background: `${DS.orange}1A`, border: `1px solid ${DS.orange}40` }}>
          {cancelaTxt}{endDate && d !== null && d !== undefined ? ` · até ${fmtDate(endDate)}` : ''}
        </span>
      )}
      {pedidoPendente && (
        <span style={{ ...seloBase, color: DS.yellow, background: `${DS.yellow}1A`, border: `1px solid ${DS.yellow}40` }}>
          Pedido de cancelamento
        </span>
      )}
    </div>
  );
};

const PlanBadge = ({ plan }) => {
  const cfg = planConfig[plan] || { label: (plan || '?').toUpperCase(), color: '#6b7280' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '6px',
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: cfg.color,
      background: `${cfg.color}1A`, border: `1px solid ${cfg.color}33`, fontFamily: DS.font, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const meta = sourceMeta[source] || { label: (source || 'admin').charAt(0).toUpperCase() + (source || 'admin').slice(1), color: DS.textSecondary };
  return (
    <span style={{ fontSize: '12px', color: meta.color, fontWeight: 500, fontFamily: DS.font }}>{meta.label}</span>
  );
};

// Card grande de destaque (Ativos / Inativos)
const BigStat = ({ label, value, color, icon: Icon, sub }) => (
  <div style={{
    position: 'relative', overflow: 'hidden', borderRadius: DS.radius, padding: '22px 24px',
    background: DS.bgCard, border: `1px solid ${DS.borderSubtle}`, boxShadow: DS.shadowCard, fontFamily: DS.font,
  }}>
    {/* Barra superior padrão (cor semântica segue no ícone/valor) */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)', opacity: 0.6 }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: DS.textSecondary, marginBottom: '8px' }}>{label}</div>
        <div style={{ fontSize: '40px', fontWeight: 700, color: DS.textPrimary, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: '12px', color: DS.textTertiary, marginTop: '8px' }}>{sub}</div>}
      </div>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={26} style={{ color, opacity: 0.85 }} />
      </div>
    </div>
  </div>
);

// Chip compacto por plano
const PlanChip = ({ plan, count }) => {
  const cfg = planConfig[plan] || { label: plan.toUpperCase(), color: DS.textSecondary };
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: DS.radiusSm,
      background: DS.bgCard, border: `1px solid ${DS.borderSubtle}`, fontFamily: DS.font,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color }} />
      <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cfg.label}</span>
      <span style={{ fontSize: '14px', fontWeight: 700, color: DS.textPrimary }}>{count}</span>
    </div>
  );
};

const ModalOverlay = ({ children, onClose, maxWidth = 480 }) => (
  <div className="dp-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', padding: '16px' }}
    onClick={onClose}>
    <div style={{ background: DS.bgCard, border: `1px solid ${DS.borderMedium}`, borderRadius: DS.radiusLg, maxWidth, width: '100%', maxHeight: '90vh', overflow: 'auto', fontFamily: DS.font }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

// Padrão premium .dp-btn-cta (ver DESIGN_SYSTEM.md §5) — versão função p/ overrides.
const primaryBtn = (extra = {}) => ({
  padding: '9px 18px', borderRadius: '12px', background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
  border: 'none', color: '#fff', fontSize: '13px', fontWeight: 600, letterSpacing: '0.2px', cursor: 'pointer', fontFamily: DS.font,
  boxShadow: 'none', transition: 'all 0.2s ease', display: 'inline-flex', alignItems: 'center', gap: 8, ...extra,
});
const ghostBtn = (extra = {}) => ({
  padding: '8px 18px', borderRadius: DS.radiusSm, background: 'transparent', border: `1px solid ${DS.borderMedium}`,
  color: DS.textSecondary, fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: DS.font,
  display: 'inline-flex', alignItems: 'center', gap: 6, ...extra,
});

// Confirm simples (reativar)
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmLabel = 'Confirmar', danger = false }) => (
  <ModalOverlay onClose={onCancel} maxWidth={420}>
    <div style={{ padding: '28px' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 600, color: DS.textPrimary }}>{title}</h3>
      <p style={{ margin: '0 0 24px', fontSize: '14px', color: DS.textSecondary, lineHeight: 1.5 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={ghostBtn()}>Cancelar</button>
        <button onClick={onConfirm} style={primaryBtn(danger ? { background: DS.red } : {})}>{confirmLabel}</button>
      </div>
    </div>
  </ModalOverlay>
);

// Diálogo de CANCELAMENTO unificado (imediato | programado + motivo)
const CancelDialog = ({ item, onClose, onConfirm }) => {
  const [mode, setMode] = useState('imediato');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const ok = motivo.trim().length > 0;

  const submit = async () => {
    if (!ok || loading) return;
    setLoading(true);
    try { await onConfirm(item.email, mode, motivo.trim()); }
    finally { setLoading(false); }
  };

  const Option = ({ value, title, desc }) => {
    const active = mode === value;
    return (
      <button onClick={() => setMode(value)} style={{
        flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: DS.radiusSm, cursor: 'pointer',
        background: active ? 'rgba(13,174,255,0.10)' : 'transparent',
        border: `1px solid ${active ? 'rgba(13,174,255,0.45)' : DS.borderMedium}`, fontFamily: DS.font, transition: 'all 0.15s',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: active ? DS.accent : DS.textPrimary }}>{title}</div>
        <div style={{ fontSize: '11px', color: DS.textTertiary, marginTop: 3, lineHeight: 1.35 }}>{desc}</div>
      </button>
    );
  };

  return (
    <ModalOverlay onClose={onClose} maxWidth={460}>
      <div style={{ padding: '28px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 600, color: DS.textPrimary }}>Cancelar assinatura</h3>
        <p style={{ margin: '0 0 18px', fontSize: '13px', color: DS.textSecondary, wordBreak: 'break-all' }}>{item.email}</p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          <Option value="imediato" title="Imediato" desc="Corta o acesso agora." />
          <Option value="programado" title="Na data de vencimento" desc="Mantém o acesso até expirar." />
        </div>

        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Motivo (obrigatório)</label>
        <textarea autoFocus value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
          placeholder="Ex.: solicitação do cliente, fraude, teste..."
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', padding: '9px 11px', borderRadius: DS.radiusSm, background: 'rgba(255,255,255,0.04)', border: `1px solid ${DS.borderMedium}`, color: DS.textPrimary, fontSize: '13px', fontFamily: DS.font, outline: 'none', marginBottom: 22 }} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn()}>Voltar</button>
          <button onClick={submit} disabled={!ok || loading}
            style={primaryBtn({ background: DS.red, cursor: ok && !loading ? 'pointer' : 'not-allowed', opacity: ok && !loading ? 1 : 0.5 })}>
            <Ban size={14} /> {loading ? 'Cancelando...' : 'Confirmar cancelamento'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

// Adicionar usuário / atribuir plano (quando presetEmail vem de um free)
const AddModal = ({ onClose, onAdd, presetEmail }) => {
  const [email, setEmail] = useState(presetEmail || '');
  const [plan, setPlan] = useState('pro');
  const [loading, setLoading] = useState(false);
  const isAssign = !!presetEmail;

  const handleSubmit = async () => {
    if (!email.trim()) { toast.error('Email é obrigatório'); return; }
    setLoading(true);
    try { await onAdd(email.trim().toLowerCase(), plan); } finally { setLoading(false); }
  };

  return (
    <ModalOverlay onClose={onClose} maxWidth={460}>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: DS.textPrimary }}>{isAssign ? 'Atribuir plano' : 'Adicionar usuário'}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: DS.textTertiary, cursor: 'pointer', padding: 4 }}><X size={20} /></button>
        </div>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Email do usuário</label>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="usuario@email.com" disabled={isAssign}
          style={{ width: '100%', padding: '10px 14px', borderRadius: DS.radiusSm, border: `1px solid ${DS.borderMedium}`, background: DS.bgInput, color: isAssign ? DS.textSecondary : DS.textPrimary, fontSize: '14px', fontFamily: DS.font, marginBottom: 16, boxSizing: 'border-box', outline: 'none', opacity: isAssign ? 0.8 : 1 }} />
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Plano</label>
        <CustomDropdown options={addPlanOptions} value={plan} onChange={setPlan} placeholder="Selecionar plano..." />
        <div style={{ padding: '10px 14px', borderRadius: DS.radiusSm, background: 'rgba(13,174,255,0.06)', border: '1px solid rgba(13,174,255,0.12)', color: DS.textSecondary, fontSize: '13px', marginTop: 16 }}>
          A assinatura é criada como ATIVA (origem: manual) e começa imediatamente.
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn()}>Cancelar</button>
          <button onClick={handleSubmit} disabled={loading} style={primaryBtn({ opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' })}>
            {loading ? 'Adicionando...' : 'Adicionar'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

// Editar limites de áudio
const AudioLimitsModal = ({ item, onClose, onSave }) => {
  const [audioLimit, setAudioLimit] = useState(item.audio_limit_override || '');
  const [charLimit, setCharLimit] = useState(item.character_limit_override || '');
  const [loading, setLoading] = useState(false);
  const handleSave = async () => {
    setLoading(true);
    try { await onSave(item.email, audioLimit || null, charLimit || null); } finally { setLoading(false); }
  };
  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: DS.radiusSm, border: `1px solid ${DS.borderMedium}`, background: DS.bgInput, color: DS.textPrimary, fontSize: '14px', fontFamily: DS.font, marginBottom: 16, boxSizing: 'border-box', outline: 'none' };
  return (
    <ModalOverlay onClose={onClose} maxWidth={440}>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: DS.textPrimary, wordBreak: 'break-all' }}>Limites de áudio</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: DS.textTertiary, cursor: 'pointer', padding: 4 }}><X size={20} /></button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '12px', color: DS.textTertiary, wordBreak: 'break-all' }}>{item.email}</p>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Áudios por dia (vazio = padrão do plano)</label>
        <input value={audioLimit} onChange={e => setAudioLimit(e.target.value)} type="number" placeholder="Padrão do plano" style={inputStyle} />
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Caracteres por áudio (vazio = padrão do plano)</label>
        <input value={charLimit} onChange={e => setCharLimit(e.target.value)} type="number" placeholder="Padrão do plano" style={inputStyle} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtn()}>Cancelar</button>
          <button onClick={handleSave} disabled={loading} style={primaryBtn({ opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' })}>
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

// Editar plano
const EditPlanModal = ({ item, onClose, onSave }) => {
  const [plan, setPlan] = useState(item.plan_type || 'pro');
  const [loading, setLoading] = useState(false);
  const handleSave = async () => {
    setLoading(true);
    try { await onSave(item.email, plan); } finally { setLoading(false); }
  };
  return (
    <ModalOverlay onClose={onClose} maxWidth={440}>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: DS.textPrimary }}>Editar plano</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: DS.textTertiary, cursor: 'pointer', padding: 4 }}><X size={20} /></button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '12px', color: DS.textTertiary, wordBreak: 'break-all' }}>{item.email}</p>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: DS.textSecondary, marginBottom: 6 }}>Novo plano (recalcula a validade)</label>
        <CustomDropdown options={addPlanOptions} value={plan} onChange={setPlan} placeholder="Selecionar plano..." />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} style={ghostBtn()}>Cancelar</button>
          <button onClick={handleSave} disabled={loading} style={primaryBtn({ opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' })}>
            {loading ? 'Salvando...' : 'Salvar plano'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

// ════════════════════════════════════════════════════════════════
// PAINEL LATERAL DE DETALHE (desliza da direita)
// ════════════════════════════════════════════════════════════════
const DetailDrawer = ({ row, data, loading, isMobile, onClose, onCancel, onReactivate, onEditPlan, onEditAudio, onAssignPlan }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShown(true), 10); return () => clearTimeout(t); }, []);
  const close = () => { setShown(false); setTimeout(onClose, 240); };

  const ae = (data && data.authorized_email) || row || {};
  const journey = (data && data.journey) || [];
  const jornadaEventos = (data && data.jornada_eventos) || [];
  const estado = (data && data.estado_efetivo) || ae.estado_efetivo || (row && row.estado_efetivo) || 'FREE';
  const estadoMotivo = (data && data.estado_motivo) || ae.estado_motivo;
  const cadastro = (data && data.cadastro) || (data && data.user && data.user.created_at) || null;
  const isFreePuro = estado === 'FREE';
  const temConta = !!(data && data.user) || ae.user_registered;
  const phone = formatPhone(ae.customer_phone);
  const renewal = getRenewalInfo(ae);
  const payment = fmtPayment(ae);

  const Section = ({ children }) => (
    <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: DS.textTertiary, margin: '22px 0 12px', paddingBottom: '7px', borderBottom: `1px solid ${DS.borderSubtle}` }}>{children}</div>
  );
  const Row = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', gap: 12 }}>
      <span style={{ fontSize: '12.5px', color: DS.textSecondary }}>{label}</span>
      <span style={{ fontSize: '12.5px', color: color || DS.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '62%', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 0.24s ease' }} />
      <aside style={{
        position: 'absolute', top: 0, right: 0, height: '100%', width: isMobile ? '100%' : 480, maxWidth: '100%',
        background: DS.bgPage, borderLeft: `1px solid ${DS.borderMedium}`, boxShadow: '-12px 0 40px rgba(0,0,0,0.4)',
        transform: shown ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.26s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column', fontFamily: DS.font,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 22px', borderBottom: `1px solid ${DS.borderSubtle}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: DS.textPrimary, wordBreak: 'break-all' }}>{ae.customer_name || ae.username || ae.email}</div>
            <div style={{ fontSize: '12px', color: DS.textTertiary, wordBreak: 'break-all', marginTop: 2 }}>{ae.email}</div>
          </div>
          <button onClick={close} style={{ background: 'transparent', border: 'none', color: DS.textTertiary, cursor: 'pointer', padding: 4, flexShrink: 0 }}><X size={20} /></button>
        </div>

        {/* Scroll body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 22px 28px' }}>
          {/* Top badges + info — ESTADO EFETIVO em destaque */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
            <EstadoBadge estado={estado} motivo={estadoMotivo} cancellationRequested={ae.cancellation_requested} autoRenew={ae.auto_renew} cancelRequestPending={ae.cancel_request_pending} endDate={ae.end_date} diasParaRenovar={ae.dias_para_renovar} />
            {!isFreePuro && <PlanBadge plan={ae.plan_type} />}
            {!isFreePuro && <SourceBadge source={ae.source} />}
          </div>

          <Section>Resumo</Section>
          <Row label="Plano atual" value={((data && data.plano_atual) || 'free').toUpperCase()} />
          <Row label="Status geral" value={(data && data.status_geral) || 'FREE'} color={((data && data.status_geral) === 'PREMIUM') ? DS.green : DS.textTertiary} />
          {cadastro && <Row label="Cadastrou em" value={fmtDateTime(cadastro)} />}
          {!isFreePuro && renewal && <Row label="Renovação" value={renewal.text} color={renewal.color} />}
          {phone && <Row label="Telefone" value={phone} />}
          {!isFreePuro && <Row label="Vencimento" value={fmtDate(ae.end_date)} />}
          {!isFreePuro && <Row label="Próx. cobrança" value={fmtDate(ae.next_charge_date)} />}
          {!isFreePuro && <Row label="Auto-renovação" value={ae.auto_renew ? 'Sim' : 'Não'} />}
          {!isFreePuro && payment && <Row label="Pagamento" value={payment} />}
          {ae.cakto_affiliate_email && <Row label="Afiliado" value={ae.cakto_affiliate_email} />}
          <Row label="Conta" value={temConta ? 'Conta criada' : 'Sem conta'} />

          {/* Ações */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
            {isFreePuro ? (
              <button onClick={() => onAssignPlan(ae.email || (row && row.email))} style={{ ...ghostBtn(), color: DS.accent, borderColor: 'rgba(13,174,255,0.3)', background: 'rgba(13,174,255,0.08)' }}><Plus size={14} /> Atribuir plano</button>
            ) : (
              <>
                {ae.status === 'ativo' ? (
                  <button onClick={() => onCancel(ae)} style={{ ...ghostBtn(), color: DS.red, borderColor: 'rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}><Ban size={14} /> Cancelar</button>
                ) : (
                  <button onClick={() => onReactivate(ae)} style={{ ...ghostBtn(), color: DS.green, borderColor: 'rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}><RotateCcw size={14} /> Reativar</button>
                )}
                <button onClick={() => onEditPlan(ae)} style={ghostBtn()}><Pencil size={14} /> Plano</button>
                <button onClick={() => onEditAudio(ae)} style={ghostBtn()}><Volume2 size={14} /> Áudio</button>
              </>
            )}
          </div>

          {/* Card "Acesso atual" removido (padronizacao): so aparecia em conta admin/manual e brigava
              com contas Cakto. O RESUMO no topo (Plano atual / Status geral / Vencimento) ja mostra o
              acesso efetivo pra TODOS, e a JORNADA abaixo mostra origem/historico completo. */}

          {/* Jornada — TODOS os eventos do subscription_history (sem omitir) + cadastro, enriquecidos */}
          <Section>Jornada</Section>
          {loading ? (
            <div style={{ fontSize: '12.5px', color: DS.textTertiary, padding: '8px 0' }}>Carregando...</div>
          ) : jornadaEventos.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: DS.textTertiary, padding: '8px 0' }}>Sem jornada registrada.</div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 18 }}>
              <div style={{ position: 'absolute', left: 5, top: 4, bottom: 4, width: 1, background: DS.borderMedium }} />
              {jornadaEventos.map((e, i) => {
                const cfg = jornadaConfig[e.change_type] || { label: e.change_type || 'Evento', color: DS.blue };
                const label = (e.change_type === 'cancelled' && e.origem === 'Admin/Manual') ? 'Plano cancelado pelo admin' : cfg.label;
                const sub = jornadaSublabel(e);
                // Reembolso: badge mostra o plano REEMBOLSADO (old_plan), nao o resultante.
                // Em refunded_access_kept o new_plan e a sub que sobrou (refletida) — confunde na linha de reembolso.
                const isRefund = e.change_type === 'refunded' || e.change_type === 'refunded_access_kept';
                const badgePlan = isRefund ? (e.old_plan || e.plan) : e.plan;
                return (
                  <div key={i} style={{ position: 'relative', paddingBottom: 16 }}>
                    <span style={{ position: 'absolute', left: -16, top: 4, width: 9, height: 9, borderRadius: '50%', background: cfg.color, border: `2px solid ${DS.bgPage}` }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: '12.5px', fontWeight: 600, color: DS.textPrimary }}>{label}</span>
                      <span style={{ fontSize: '11px', color: DS.textTertiary, whiteSpace: 'nowrap' }}>{fmtDateTime(e.date)}</span>
                    </div>
                    {sub && <div style={{ fontSize: '11px', color: DS.textTertiary, marginTop: 1 }}>{sub}</div>}
                    <div style={{ fontSize: '11px', color: DS.textTertiary, marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {badgePlan && <PlanBadge plan={badgePlan} />}
                      <span style={{ color: jornadaOrigemColor(e.origem), fontWeight: 500 }}>{e.origem}</span>
                      {e.admin && <span style={{ wordBreak: 'break-all' }}>· {e.admin}</span>}
                    </div>
                    {e.motivo && <div style={{ fontSize: '11px', color: DS.textTertiary, marginTop: 3, fontStyle: 'italic', wordBreak: 'break-word' }}>{e.motivo}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
const SuperAdminNew = ({ onLogout }) => {
  usePageTitle('Super Admin');
  const { isMobile, sidebarWidth } = useSidebar();
  // Papéis: super admin vê TUDO; moderador vê a gestão de usuários (o backend
  // barra o resto). Só o super admin gerencia chaves e concede/revoga moderador.
  const { isSuperAdmin } = useAdmin();

  const [users, setUsers] = useState([]);   // página atual da LISTA ÚNICA (vem do backend)
  const [total, setTotal] = useState(0);    // total do conjunto filtrado (vem do backend)
  const [statsApi, setStatsApi] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtros / busca / paginação (todos server-side via /super-admin/users)
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');   // ''|PREMIUM|CANCELADO|REEMBOLSADO|FREE
  const [filterPlan, setFilterPlan] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [page, setPage] = useState(1);

  // Modais / painel
  const [detailRow, setDetailRow] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPreset, setAddPreset] = useState(null);   // email pré-preenchido (atribuir plano a um free)
  const [audioItem, setAudioItem] = useState(null);
  const [editPlanItem, setEditPlanItem] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { item, x, y }

  // Gemini global
  const [geminiKey, setGeminiKey] = useState('');
  const [geminiStatus, setGeminiStatus] = useState({ has_key: false, masked_key: null });
  const [geminiUsage, setGeminiUsage] = useState({ today_users: 0, today_total: 0 });
  const [geminiLoading, setGeminiLoading] = useState(false);

  // Solicitações de cancelamento feitas no app (fila p/ processar na Cakto)
  const [cancelRequests, setCancelRequests] = useState([]);

  // ── Fetch ──────────────────────────────────────────────────────
  // LISTA ÚNICA: pagantes + free juntos, paginado e filtrado NO BACKEND.
  const fetchUsers = useCallback(async (pg, { estado, plano, origem, search }) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String((pg - 1) * PAGE_SIZE) });
      if (estado) params.set('estado', estado);
      if (plano) params.set('plano', plano);
      if (origem) params.set('origem', origem);
      if (search) params.set('search', search);
      const res = await fetch(`${getApiUrl()}/api/super-admin/users?${params.toString()}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(`Erro ao carregar dados: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/super-admin/stats`, { headers: getAuthHeaders() });
      if (res.ok) setStatsApi(await res.json());
    } catch { /* silencioso: cards ficam zerados até o /stats responder */ }
  }, []);

  const fetchGeminiConfig = useCallback(async () => {
    try {
      const [keyRes, usageRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/admin/config/gemini-key`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/admin/config/gemini-usage`, { headers: getAuthHeaders() }),
      ]);
      if (keyRes.ok) setGeminiStatus(await keyRes.json());
      if (usageRes.ok) setGeminiUsage(await usageRes.json());
    } catch { /* silent */ }
  }, []);

  // Fila de cancelamentos pendentes: o usuário solicitou no app e o admin ainda
  // não cancelou na Cakto. Quando o webhook subscription_canceled processa
  // (auto_renew→false), a linha sai sozinha da fila (filtro no backend).
  const fetchCancelRequests = useCallback(async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/super-admin/cancel-requests`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCancelRequests(data.items || []);
      }
    } catch { /* silent */ }
  }, []);

  const fetchUserDetail = useCallback(async (email) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/super-admin/user-detail/${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
      if (res.ok) setDetailData(await res.json());
      else setDetailData(null);
    } catch { setDetailData(null); }
    finally { setDetailLoading(false); }
  }, []);

  // Stats + Gemini + fila de cancelamentos no mount. Contadores SEMPRE do /stats.
  useEffect(() => { fetchStats(); fetchGeminiConfig(); fetchCancelRequests(); }, [fetchStats, fetchGeminiConfig, fetchCancelRequests]);
  // Trocar filtro/busca → volta à página 1.
  useEffect(() => { setPage(1); }, [searchTerm, filterEstado, filterPlan, filterSource]);
  // LISTA ÚNICA: GET /super-admin/users quando página/filtros/busca mudam. Debounce 250ms
  // coalesce a dupla disparada (mudança de filtro + reset de página) numa só requisição.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchUsers(page, { estado: filterEstado, plano: filterPlan, origem: filterSource, search: searchTerm.trim() });
    }, 250);
    return () => clearTimeout(t);
  }, [page, filterEstado, filterPlan, filterSource, searchTerm, fetchUsers]);

  // ── Helpers de ação ────────────────────────────────────────────
  const apiCall = useCallback(async (url, options = {}) => {
    const res = await fetch(`${getApiUrl()}${url}`, { ...options, headers: getAuthHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || body.message || `HTTP ${res.status}`);
    }
    return res.json();
  }, []);

  const refresh = useCallback((email) => {
    fetchUsers(page, { estado: filterEstado, plano: filterPlan, origem: filterSource, search: searchTerm.trim() });
    fetchStats();
    if (email) fetchUserDetail(email);
  }, [fetchUsers, fetchStats, fetchUserDetail, page, filterEstado, filterPlan, filterSource, searchTerm]);

  const openDetail = useCallback((item) => {
    setDetailRow(item);
    setDetailData(null);
    fetchUserDetail(item.email);
  }, [fetchUserDetail]);

  const handleAdd = useCallback(async (email, plan) => {
    try {
      await apiCall('/api/super-admin/authorized-emails', { method: 'POST', body: JSON.stringify({ email, plan_type: plan }) });
      toast.success(`${email}: plano ${String(plan).toUpperCase()} atribuído`);
      setShowAddModal(false);
      setAddPreset(null);
      refresh();   // atribuir plano a um free move a linha p/ pagante na própria lista
    } catch (err) { toast.error(`Erro: ${err.message}`); }
  }, [apiCall, refresh]);

  const handleUpdatePlan = useCallback(async (email, newPlan) => {
    try {
      await apiCall(`/api/super-admin/authorized-emails/${encodeURIComponent(email)}`, { method: 'PUT', body: JSON.stringify({ plan_type: newPlan }) });
      toast.success(`Plano de ${email} alterado para ${newPlan.toUpperCase()}`);
      setEditPlanItem(null);
      refresh(email);
    } catch (err) { toast.error(`Erro: ${err.message}`); }
  }, [apiCall, refresh]);

  const handleSaveAudio = useCallback(async (email, audioLimit, charLimit) => {
    try {
      await apiCall(`/api/super-admin/authorized-emails/${encodeURIComponent(email)}/audio-limits`, {
        method: 'PUT',
        body: JSON.stringify({ audio_limit_override: audioLimit ? parseInt(audioLimit, 10) : null, character_limit_override: charLimit ? parseInt(charLimit, 10) : null }),
      });
      toast.success(`Limites de áudio atualizados`);
      setAudioItem(null);
      refresh(email);
    } catch (err) { toast.error(`Erro: ${err.message}`); }
  }, [apiCall, refresh]);

  const handleCancel = useCallback(async (email, mode, motivo) => {
    try {
      await apiCall('/api/super-admin/cancel-subscription', { method: 'POST', body: JSON.stringify({ email, mode, motivo }) });
      toast.success(mode === 'imediato' ? `${email} cancelado` : `${email}: cancelamento agendado`);
      setCancelItem(null);
      refresh(email);
    } catch (err) { toast.error(`Erro: ${err.message}`); }
  }, [apiCall, refresh]);

  const handleReactivate = useCallback((item) => {
    setConfirmState({
      title: 'Reativar assinatura',
      message: `Reativar o acesso de ${item.email}?`,
      confirmLabel: 'Reativar',
      onConfirm: async () => {
        try {
          await apiCall(`/api/super-admin/authorized-emails/${encodeURIComponent(item.email)}/reactivate`, { method: 'POST' });
          toast.success(`${item.email} reativado`);
          setConfirmState(null);
          refresh(item.email);
        } catch (err) { toast.error(`Erro: ${err.message}`); setConfirmState(null); }
      },
    });
  }, [apiCall, refresh]);

  // Concede/revoga o papel MODERADOR (endpoint é super-admin-only; auditado).
  const handleToggleModerator = useCallback(async (item) => {
    try {
      const next = !item.is_moderator;
      await apiCall('/api/super-admin/users/moderator', {
        method: 'PUT',
        body: JSON.stringify({ email: item.email, is_moderator: next }),
      });
      toast.success(next ? `${item.email} agora é MODERADOR` : `Papel de moderador removido de ${item.email}`);
      refresh(item.email);
    } catch (err) { toast.error(`Erro: ${err.message}`); }
  }, [apiCall, refresh]);

  const openRowMenu = (e, item) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setRowMenu({ item, x: r.right, y: r.bottom + 4 });
  };

  // ── Derivados — TODOS do /stats (a lista é paginada no backend, não dá p/ derivar dela) ──
  // Distribuição por plano dos PAGANTES VÁLIDOS (PREMIUM) — /stats.premium_por_plano.
  const planDist = useMemo(() => {
    const counts = (statsApi && statsApi.premium_por_plano) || {};
    const known = new Set([...PLAN_CHIPS]);
    const outros = Object.entries(counts).filter(([p]) => !known.has(p)).reduce((s, [, c]) => s + c, 0);
    return { counts, outros };   // master/trial/starter/creator/... caem em "outros"
  }, [statsApi]);

  // Números por ESTADO EFETIVO — exclusivamente do /stats (já sem o super admin).
  const estados = useMemo(() => {
    const pe = (statsApi && statsApi.por_estado_efetivo) || {};
    // CANCELADO já engloba o antigo EXPIRADO (backend colapsou). Somo pe.EXPIRADO só por
    // robustez a um /stats legado em cache — hoje vem 0.
    const cancelado = (pe.CANCELADO || 0) + (pe.EXPIRADO || 0);
    const reembolsado = pe.REEMBOLSADO || 0;
    const freePuro = (statsApi && statsApi.total_free_puro) || 0;
    return {
      premium: pe.PREMIUM || 0, cancelado, reembolsado, freePuro,
      freePratica: freePuro + cancelado + reembolsado,
    };
  }, [statsApi]);

  const cancelando = (statsApi && statsApi.cancelando) || 0;

  // Paginação dirigida pelo backend (total do conjunto filtrado).
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  // ── Gemini actions ─────────────────────────────────────────────
  const saveGeminiKey = async () => {
    if (!geminiKey.trim()) return;
    setGeminiLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/admin/config/gemini-key`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ api_key: geminiKey.trim() }) });
      if (res.ok) { toast.success('API Gemini global configurada'); setGeminiKey(''); fetchGeminiConfig(); }
      else toast.error('Erro ao salvar API key');
    } catch { toast.error('Erro de conexão'); }
    finally { setGeminiLoading(false); }
  };
  const deleteGeminiKey = async () => {
    setGeminiLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/admin/config/gemini-key`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('API Gemini global removida'); fetchGeminiConfig(); }
    } catch { toast.error('Erro ao remover'); }
    finally { setGeminiLoading(false); }
  };

  // ── Linha da tabela (trata pagante E free numa só) ─────────────
  const Dash = () => <span style={{ fontSize: '12px', color: DS.textTertiary }}>—</span>;
  const Row = ({ item }) => {
    const isFree = item.estado_efetivo === 'FREE';
    const phone = formatPhone(item.customer_phone);
    const renewal = isFree ? null : getRenewalInfo(item);
    const nome = item.customer_name || item.full_name;
    return (
      <tr onClick={() => openDetail(item)} style={{ borderBottom: `1px solid ${DS.borderSubtle}`, cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <td style={{ padding: '13px 12px', verticalAlign: 'top' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: DS.textPrimary, wordBreak: 'break-all' }}>
            {item.email}
            {item.is_moderator && (
              <span style={{ fontSize: 9, fontWeight: 700, color: '#0DAEFF', border: '1px solid rgba(13,174,255,0.35)', background: 'rgba(13,174,255,0.08)', borderRadius: 6, padding: '1px 6px', marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.5px' }}>MOD</span>
            )}
          </div>
          {nome && <div style={{ fontSize: '12px', color: DS.textSecondary, marginTop: 2 }}>{nome}</div>}
          {phone && <div style={{ fontSize: '11px', color: DS.textTertiary, marginTop: 1 }}>{phone}</div>}
        </td>
        <td style={{ padding: '13px 8px', verticalAlign: 'middle' }}>{item.estado_efetivo === 'PREMIUM' ? <PlanBadge plan={item.plan_type} /> : <Dash />}</td>
        <td style={{ padding: '13px 8px', verticalAlign: 'middle' }}><EstadoBadge estado={item.estado_efetivo} motivo={item.estado_motivo} cancellationRequested={item.cancellation_requested} autoRenew={item.auto_renew} cancelRequestPending={item.cancel_request_pending} endDate={item.end_date} diasParaRenovar={item.dias_para_renovar} /></td>
        <td style={{ padding: '13px 8px', verticalAlign: 'middle' }}>{item.estado_efetivo === 'PREMIUM' ? <SourceBadge source={item.source} /> : <Dash />}</td>
        <td style={{ padding: '13px 8px', verticalAlign: 'middle' }}>
          {renewal ? <span style={{ fontSize: '12px', color: renewal.color, fontWeight: 500 }}>{renewal.text}</span> : <Dash />}
        </td>
        <td style={{ padding: '13px 8px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => openDetail(item)} title="Ver detalhe" style={iconBtn('rgba(13,174,255,0.08)', 'rgba(13,174,255,0.15)', '#0DAEFF')}><Eye size={14} /></button>
            {isFree ? (
              <button onClick={() => { setAddPreset(item.email); setShowAddModal(true); }} title="Atribuir plano" style={iconBtn('rgba(52,211,153,0.08)', 'rgba(52,211,153,0.15)', DS.green)}><Plus size={14} /></button>
            ) : (
              <>
                <button onClick={() => setAudioItem(item)} title="Limites de áudio" style={iconBtn('rgba(167,139,250,0.08)', 'rgba(167,139,250,0.15)', DS.purple)}><Volume2 size={14} /></button>
                <button onClick={(e) => openRowMenu(e, item)} title="Mais ações" style={iconBtn('rgba(255,255,255,0.04)', DS.borderMedium, DS.textSecondary)}><MoreVertical size={14} /></button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ── Card mobile (pagante E free) ───────────────────────────────
  const MobileCard = ({ item }) => {
    const isFree = item.estado_efetivo === 'FREE';
    const phone = formatPhone(item.customer_phone);
    const renewal = isFree ? null : getRenewalInfo(item);
    const nome = item.customer_name || item.full_name;
    return (
      <div onClick={() => openDetail(item)} style={{ background: DS.bgCard, borderRadius: DS.radius, border: `1px solid ${DS.borderSubtle}`, padding: '14px', marginBottom: 10, fontFamily: DS.font, cursor: 'pointer' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: DS.textPrimary, wordBreak: 'break-all' }}>{item.email}</div>
        {nome && <div style={{ fontSize: '12px', color: DS.textSecondary, marginTop: 2 }}>{nome}{phone ? ` · ${phone}` : ''}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 10 }}>
          {item.estado_efetivo === 'PREMIUM' && <PlanBadge plan={item.plan_type} />}
          <EstadoBadge estado={item.estado_efetivo} motivo={item.estado_motivo} cancellationRequested={item.cancellation_requested} autoRenew={item.auto_renew} cancelRequestPending={item.cancel_request_pending} endDate={item.end_date} diasParaRenovar={item.dias_para_renovar} />
          {item.estado_efetivo === 'PREMIUM' && <SourceBadge source={item.source} />}
          {renewal && <span style={{ fontSize: '11px', color: renewal.color, fontWeight: 500 }}>{renewal.text}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => openDetail(item)} style={{ flex: 1, ...iconBtnWide('rgba(13,174,255,0.08)', 'rgba(13,174,255,0.15)', '#0DAEFF') }}><Eye size={14} /> Detalhe</button>
          {isFree ? (
            <button onClick={() => { setAddPreset(item.email); setShowAddModal(true); }} style={iconBtnWide('rgba(52,211,153,0.08)', 'rgba(52,211,153,0.15)', DS.green)}><Plus size={14} /> Plano</button>
          ) : (
            <>
              <button onClick={() => setAudioItem(item)} style={iconBtn('rgba(167,139,250,0.08)', 'rgba(167,139,250,0.15)', DS.purple)}><Volume2 size={14} /></button>
              <button onClick={(e) => openRowMenu(e, item)} style={iconBtn('rgba(255,255,255,0.04)', DS.borderMedium, DS.textSecondary)}><MoreVertical size={14} /></button>
            </>
          )}
        </div>
      </div>
    );
  };

  const Th = ({ children, width }) => (
    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: DS.textTertiary, whiteSpace: 'nowrap', width, borderBottom: `1px solid ${DS.borderMedium}` }}>{children}</th>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`, marginTop: '60px', flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s ease', fontFamily: 'var(--dp-font)', boxSizing: 'border-box', animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Header padrão do sistema */}
        <PageHeader
          icon={<Shield size={22} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title="Super Admin"
          description="Gerenciamento de assinaturas e usuários"
        >
          <button className="dp-btn-cta dp-btn-cta--sm" onClick={() => { fetchUsers(page, { estado: filterEstado, plano: filterPlan, origem: filterSource, search: searchTerm.trim() }); fetchStats(); fetchCancelRequests(); }} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button className="dp-btn-cta dp-btn-cta--sm" onClick={() => setShowAddModal(true)}><Plus size={14} /> Adicionar usuário <ArrowRight size={14} /></button>
        </PageHeader>

        {/* Dashboard: PREMIUM x FREE (estado EFETIVO) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <BigStat label="Premium (pagantes válidos)" value={estados.premium} color={DS.green} icon={CheckCircle} sub={cancelando > 0 ? `${cancelando} com cancelamento pedido` : null} />
          <BigStat label="Free (na prática)" value={estados.freePratica} color={DS.blue} icon={Users} sub={`${estados.freePuro} nunca pagaram`} />
        </div>

        {/* Distribuição por plano dos PAGANTES VÁLIDOS (compacto) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 2 }}>Pagantes:</span>
          {PLAN_CHIPS.map(p => <PlanChip key={p} plan={p} count={planDist.counts[p] || 0} />)}
          {planDist.outros > 0 && <PlanChip plan="outros" count={planDist.outros} />}
        </div>

        {/* Linha fina de estados não-premium (Cancelado já engloba os antigos expirados) */}
        {(estados.cancelado + estados.reembolsado) > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginBottom: 16, padding: '9px 16px', background: 'var(--dp-bg-card)', borderRadius: DS.radiusSm, border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 0' }}>
            <span style={{ fontWeight: 600, marginRight: 10, color: DS.textSecondary }}>Não-premium:</span>
            <span style={{ color: DS.purple }}>Cancelado {estados.cancelado}</span>
            <span style={{ margin: '0 10px', opacity: 0.4 }}>·</span>
            <span style={{ color: DS.pink }}>Reembolsado {estados.reembolsado}</span>
          </div>
        )}

        {/* SOLICITAÇÕES DE CANCELAMENTO (feitas no app, aguardando ação na Cakto) */}
        <div className="dp-top-accent" style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))', borderRadius: DS.radiusSm, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(251, 146, 60, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <UserX size={16} style={{ color: DS.orange }} />
              </div>
              <div>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>Solicitações de cancelamento</span>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                  Pedidos feitos pelo usuário em Meu Plano. Cancele na Cakto; quando o webhook confirmar, a linha sai sozinha da fila.
                </p>
              </div>
            </div>
            <span style={{
              fontSize: '11px', fontWeight: 700, padding: '3px 11px', borderRadius: '9999px',
              background: cancelRequests.length > 0 ? 'rgba(251, 146, 60, 0.12)' : 'rgba(52, 211, 153, 0.10)',
              color: cancelRequests.length > 0 ? DS.orange : DS.green,
              border: `1px solid ${cancelRequests.length > 0 ? 'rgba(251, 146, 60, 0.30)' : 'rgba(52, 211, 153, 0.25)'}`,
            }}>
              {cancelRequests.length > 0 ? `${cancelRequests.length} pendente${cancelRequests.length > 1 ? 's' : ''}` : 'Nenhuma pendente'}
            </span>
          </div>

          {cancelRequests.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 14 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: DS.font, minWidth: '760px' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                    <Th>Email / Usuário</Th>
                    <Th width="90px">Plano</Th>
                    <Th width="90px">Origem</Th>
                    <Th width="190px">Assinatura (gateway)</Th>
                    <Th width="140px">Solicitado em</Th>
                    <Th width="110px">Ativo até</Th>
                    <Th width="90px">Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {cancelRequests.map((r) => {
                    const subId = r.cakto_subscription_id || r.cakto_sale_id || r.kirvano_sale_id;
                    return (
                      <tr key={r.email} style={{ borderBottom: `1px solid ${DS.borderSubtle}` }}>
                        <td style={{ padding: '11px 12px', verticalAlign: 'top' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: DS.textPrimary, wordBreak: 'break-all' }}>{r.email}</div>
                          {r.nome && <div style={{ fontSize: '12px', color: DS.textSecondary, marginTop: 2 }}>{r.nome}{formatPhone(r.telefone) ? ` · ${formatPhone(r.telefone)}` : ''}</div>}
                        </td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle' }}><PlanBadge plan={r.plan_type} /></td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle' }}><SourceBadge source={r.source} /></td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle' }}>
                          {subId ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <code style={{ fontSize: '11px', color: DS.textSecondary, fontFamily: 'monospace', wordBreak: 'break-all' }}>{subId}</code>
                              <button
                                title="Copiar ID"
                                onClick={() => { navigator.clipboard.writeText(subId); toast.success('ID copiado'); }}
                                style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: DS.textTertiary, display: 'inline-flex' }}
                              >
                                <Copy size={12} />
                              </button>
                            </span>
                          ) : <span style={{ fontSize: '12px', color: DS.textTertiary }}>—</span>}
                        </td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle', fontSize: '12px', color: DS.orange, fontWeight: 500 }}>{fmtDateTime(r.requested_at)}</td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle', fontSize: '12px', color: DS.textSecondary }}>{fmtDate(r.end_date)}</td>
                        <td style={{ padding: '11px 8px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => openDetail({ email: r.email })} title="Ver detalhe" style={iconBtn('rgba(13,174,255,0.08)', 'rgba(13,174,255,0.15)', '#0DAEFF')}><Eye size={14} /></button>
                            <button
                              title="Copiar email"
                              onClick={() => { navigator.clipboard.writeText(r.email); toast.success('Email copiado'); }}
                              style={iconBtn('rgba(255,255,255,0.04)', DS.borderMedium, DS.textSecondary)}
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* API Gemini Global — SÓ super admin (moderador não gerencia chaves) */}
        {isSuperAdmin && (
        <div className="dp-top-accent" style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))', borderRadius: DS.radiusSm, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(66, 133, 244, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={16} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>API Gemini Global</span>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>Chave compartilhada para reescrita de prompts (limite: 50/dia por usuário)</p>
              </div>
            </div>
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '3px 10px', borderRadius: '9999px', background: geminiStatus.has_key ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: geminiStatus.has_key ? '#22C55E' : '#EF4444', border: `1px solid ${geminiStatus.has_key ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'}` }}>
              {geminiStatus.has_key ? 'Configurada' : 'Não configurada'}
            </span>
          </div>
          {geminiStatus.has_key && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Chave Ativa</div>
                <div style={{ fontSize: '13px', color: 'var(--dp-text-primary)', fontFamily: 'monospace' }}>{geminiStatus.masked_key || '••••••••'}</div>
              </div>
              <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)', minWidth: 120 }}>
                <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Uso Hoje</div>
                <div style={{ fontSize: '13px', color: 'var(--dp-text-primary)' }}>
                  <span style={{ fontWeight: 600 }}>{geminiUsage.today_total || 0}</span>
                  <span style={{ color: 'var(--dp-text-tertiary)', fontSize: '11px' }}> rewrites</span>
                  <span style={{ color: 'var(--dp-text-tertiary)', fontSize: '11px', marginLeft: 8 }}>({geminiUsage.today_users || 0} usuários)</span>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="password" value={geminiKey} onChange={e => setGeminiKey(e.target.value)} placeholder={geminiStatus.has_key ? 'Nova chave (substituir atual)...' : 'Cole a API Key do Gemini aqui...'}
              style={{ flex: 1, minWidth: 200, padding: '9px 14px', borderRadius: 8, border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.10))', background: 'rgba(0,0,0,0.2)', color: 'var(--dp-text-primary)', fontSize: '13px', fontFamily: 'var(--dp-font)', outline: 'none' }} />
            <button onClick={saveGeminiKey} disabled={geminiLoading || !geminiKey.trim()} style={{ padding: '9px 18px', borderRadius: 12, background: geminiKey.trim() ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)' : 'rgba(255,255,255,0.05)', border: 'none', color: geminiKey.trim() ? '#fff' : 'var(--dp-text-tertiary)', fontSize: '13px', fontWeight: 600, cursor: geminiKey.trim() ? 'pointer' : 'default', fontFamily: 'var(--dp-font)', opacity: geminiLoading ? 0.5 : 1 }}>
              {geminiLoading ? '...' : 'Salvar'}
            </button>
            {geminiStatus.has_key && (
              <button onClick={deleteGeminiKey} disabled={geminiLoading} style={{ padding: '9px 14px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#EF4444', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--dp-font)', opacity: geminiLoading ? 0.5 : 1 }}>Remover</button>
            )}
          </div>
        </div>
        )}

        {/* Busca + filtros */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por email, nome ou telefone..."
              style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: DS.radiusSm, border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.10))', background: 'var(--dp-bg-card)', color: 'var(--dp-text-primary)', fontSize: '14px', fontFamily: 'var(--dp-font)', boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ minWidth: isMobile ? '100%' : 180, flex: isMobile ? '1 1 100%' : '0 0 auto' }}><CustomDropdown options={estadoOptions} value={filterEstado} onChange={setFilterEstado} placeholder="Estado" /></div>
            <div style={{ minWidth: isMobile ? '100%' : 160, flex: isMobile ? '1 1 100%' : '0 0 auto' }}><CustomDropdown options={planOptions} value={filterPlan} onChange={setFilterPlan} placeholder="Plano" /></div>
            <div style={{ minWidth: isMobile ? '100%' : 160, flex: isMobile ? '1 1 100%' : '0 0 auto' }}><CustomDropdown options={sourceOptions} value={filterSource} onChange={setFilterSource} placeholder="Origem" /></div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
            {`Mostrando ${users.length} de ${total}`}
          </div>
        </div>

        {/* LISTA ÚNICA (paginada no backend): pagantes + free juntos, estado por linha */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--dp-text-secondary)' }}>
            <RefreshCw size={28} className="animate-spin" style={{ marginBottom: 12, color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <p>Carregando usuários...</p>
          </div>
        )}
        {!loading && users.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--dp-text-tertiary)' }}>
            <Users size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p style={{ fontSize: '15px' }}>Nenhum usuário encontrado</p>
          </div>
        )}
        {!loading && users.length > 0 && (
          isMobile ? (
            <div>{users.map(item => <MobileCard key={item.id || item.email} item={item} />)}</div>
          ) : (
            <div className="dp-top-accent" style={{ background: 'var(--dp-bg-card)', borderRadius: DS.radius, border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))', overflow: 'hidden', boxShadow: DS.shadowCard }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--dp-font)', minWidth: '860px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <Th>Email / Usuário</Th>
                      <Th width="100px">Plano</Th>
                      <Th width="150px">Estado</Th>
                      <Th width="100px">Origem</Th>
                      <Th width="150px">Renovação</Th>
                      <Th width="120px">Ações</Th>
                    </tr>
                  </thead>
                  <tbody>{users.map(item => <Row key={item.id || item.email} item={item} />)}</tbody>
                </table>
              </div>
            </div>
          )
        )}
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 18 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pageSafe <= 1} style={{ ...ghostBtn({ padding: '7px 12px' }), opacity: pageSafe <= 1 ? 0.4 : 1, cursor: pageSafe <= 1 ? 'default' : 'pointer' }}><ChevronLeft size={15} /></button>
            <span style={{ fontSize: '13px', color: DS.textSecondary, fontFamily: DS.font }}>Página {pageSafe} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages} style={{ ...ghostBtn({ padding: '7px 12px' }), opacity: pageSafe >= totalPages ? 0.4 : 1, cursor: pageSafe >= totalPages ? 'default' : 'pointer' }}><ChevronRight size={15} /></button>
          </div>
        )}
      </main>

      {/* Painel lateral */}
      {detailRow && (
        <DetailDrawer
          row={detailRow}
          data={detailData}
          loading={detailLoading}
          isMobile={isMobile}
          onClose={() => { setDetailRow(null); setDetailData(null); }}
          onCancel={(ae) => setCancelItem(ae)}
          onReactivate={(ae) => handleReactivate(ae)}
          onEditPlan={(ae) => setEditPlanItem(ae)}
          onEditAudio={(ae) => setAudioItem(ae)}
          onAssignPlan={(email) => { if (email) { setAddPreset(email); setShowAddModal(true); } }}
        />
      )}

      {/* Menu de linha (cancelar / reativar / editar plano) */}
      {rowMenu && (
        <>
          <div onClick={() => setRowMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 10050 }} />
          <div style={{ position: 'fixed', top: rowMenu.y, left: Math.max(8, rowMenu.x - 190), width: 190, zIndex: 10051, background: DS.bgElevated, border: `1px solid ${DS.borderMedium}`, borderRadius: DS.radiusSm, boxShadow: DS.shadowHover, padding: 6, fontFamily: DS.font }}>
            {rowMenu.item.status === 'ativo' ? (
              <MenuItem icon={Ban} color={DS.red} label="Cancelar" onClick={() => { setCancelItem(rowMenu.item); setRowMenu(null); }} />
            ) : (
              <MenuItem icon={RotateCcw} color={DS.green} label="Reativar" onClick={() => { handleReactivate(rowMenu.item); setRowMenu(null); }} />
            )}
            <MenuItem icon={Pencil} color={DS.textSecondary} label="Editar plano" onClick={() => { setEditPlanItem(rowMenu.item); setRowMenu(null); }} />
            <MenuItem icon={Volume2} color={DS.textSecondary} label="Limites de áudio" onClick={() => { setAudioItem(rowMenu.item); setRowMenu(null); }} />
            {isSuperAdmin && (
              <MenuItem
                icon={Shield}
                color="#0DAEFF"
                label={rowMenu.item.is_moderator ? 'Remover moderador' : 'Tornar moderador'}
                onClick={() => { handleToggleModerator(rowMenu.item); setRowMenu(null); }}
              />
            )}
          </div>
        </>
      )}

      {/* Modais */}
      {showAddModal && <AddModal onClose={() => { setShowAddModal(false); setAddPreset(null); }} onAdd={handleAdd} presetEmail={addPreset} />}
      {audioItem && <AudioLimitsModal item={audioItem} onClose={() => setAudioItem(null)} onSave={handleSaveAudio} />}
      {editPlanItem && <EditPlanModal item={editPlanItem} onClose={() => setEditPlanItem(null)} onSave={handleUpdatePlan} />}
      {cancelItem && <CancelDialog item={cancelItem} onClose={() => setCancelItem(null)} onConfirm={handleCancel} />}
      {confirmState && (
        <ConfirmDialog title={confirmState.title} message={confirmState.message} confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />
      )}
    </div>
  );
};

// ── estilos util de botões de ícone ───────────────────────────────
const iconBtn = (bg, border, color) => ({
  width: 32, height: 32, borderRadius: DS.radiusSm, background: bg, border: `1px solid ${border}`,
  color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});
const iconBtnWide = (bg, border, color) => ({
  padding: '7px 10px', borderRadius: DS.radiusSm, background: bg, border: `1px solid ${border}`,
  color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  fontSize: '12px', fontWeight: 600, fontFamily: DS.font,
});

const MenuItem = ({ icon: Icon, label, color, onClick }) => (
  <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: '6px', background: 'transparent', border: 'none', color: color || DS.textSecondary, fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: DS.font, textAlign: 'left' }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
    <Icon size={15} /> {label}
  </button>
);

export default SuperAdminNew;
